/**
 * E-invoice registration, in the invoice detail's layout:
 *
 * vendor/isomorphic/apps/isomorphic/src/app/shared/logistics/tracking/tracking-overview.tsx
 *   the state panel, and its `Timeline` for the attempt history (non-ecommerce, reason in
 *   screen-map §7);
 * vendor/isomorphic/apps/isomorphic/src/app/shared/ecommerce/order/order-view.tsx
 *   the 8/4 main-and-side grid;
 * vendor/isomorphic/apps/isomorphic/src/app/shared/logistics/tracking/shipping-info.tsx
 *   icon tile + label/value blocks in the side card.
 *
 * Five states, none collapses into another (`not_applicable`/`not_configured`→pending/`pending`/
 * `registered`/`failed`/`cancelled`/`cancellation_requested`→pending) — an unrecognised word maps to
 * pending, never failed, and is always shown verbatim beside the badge. `RegistrationState` owns
 * that vocabulary; this screen only frames it.
 */
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import {
  PiArrowsClockwiseDuotone as RetryIcon,
  PiCheckCircleDuotone as DoneIcon,
  PiClockCountdownDuotone as NextIcon,
  PiClockCounterClockwiseDuotone as HistoryIcon,
  PiDotsThreeOutlineVerticalFill as MoreIcon,
  PiInfoDuotone as InfoIcon,
  PiListNumbersDuotone as CountIcon,
  PiPaperPlaneTiltDuotone as SendIcon,
  PiPrinterDuotone as PrintIcon,
  PiSealCheckDuotone as SealIcon,
  PiWarningDuotone as WarningIcon,
  PiXCircleDuotone as CancelIcon,
} from "react-icons/pi";
import { Link, useLoaderData, useNavigate, useOutletContext, useSearchParams, type LoaderFunctionArgs } from "react-router-dom";
import { Alert, Badge, Button, Dropdown, Text } from "rizzui";
import { CaveatBanner, EmptyState, ErrorSurface, ReasonDialog, RegistrationState, VerbatimErrorText, WidgetCard, type RegistrationStateName } from "@steelyard/theme";
import Timeline from "@core/app/shared/logistics/tracking/timeline";
import { CardTitle, CopyButton, InfoBlock, PageHero, iconMenuTriggerClass } from "../../components/detail-parts";
import { can } from "../../lib/permissions";
import { formatDate, formatMoment, formatTime } from "../../lib/format";
import { cancelIrn, getRegistrationStatus, registerInvoice, retryAwaitingRegistrations, windowClosedAt, type RegistrationStatus } from "../../lib/services/registration";
import { invoiceByKey, statusWords } from "../../lib/services/invoicing";
import { invoicePath } from "../../lib/paths";
import { ApiError } from "../../lib/api";
import type { AccountSummary, PermissionState } from "../../types";

const IRN_STATE: Record<string, RegistrationStateName> = {
  not_applicable: "not_applicable",
  not_configured: "pending",
  pending: "pending",
  cancellation_requested: "pending",
  registered: "registered",
  failed: "failed",
  cancelled: "cancelled",
};

/** The hero tile's colour per state — position in the process, not a second status vocabulary. */
const TILE: Record<RegistrationStateName, string> = {
  not_applicable: "bg-gray-500",
  pending: "bg-blue",
  registered: "bg-green",
  failed: "bg-red",
  cancelled: "bg-gray-500",
};

export async function registrationLoader({ params, request }: LoaderFunctionArgs) {
  const invoice = await invoiceByKey(request, params.invoiceNumber!);
  return { data: await getRegistrationStatus(invoice.id), id: invoice.id, href: invoicePath(params.invoiceNumber!) };
}

export default function RegistrationPage() {
  const { data, id, href } = useLoaderData() as { data: RegistrationStatus; id: string; href: string };
  const { permissionState } = useOutletContext<{ account: AccountSummary; permissionState: PermissionState }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [cancelError, setCancelError] = useState<string | undefined>();

  const confirmingCancel = searchParams.get("confirm") === "cancel";
  useEffect(() => {
    if (confirmingCancel) setCancelError(undefined);
  }, [confirmingCancel]);

  // Reading the status (irnError, attempts) is billing.read|dispatch.assign, as the service allows;
  // registering, retrying and cancelling stay billing.administer.
  if (!(["billing.administer", "billing.read", "dispatch.assign"] as const).some((code) => can(permissionState, code))) {
    return <ErrorSurface error={{ title: "Not permitted", status: 403 }} action="view this invoice's registration" />;
  }
  const mayCancel = can(permissionState, "billing.administer");

  const word = data.irnStatus;
  const state = IRN_STATE[word] ?? "pending";
  const mayAttempt = (state === "pending" || state === "failed") && mayCancel;

  async function handleRegister() {
    setBusy(true);
    try {
      const result = await registerInvoice(id);
      navigate(`${href}/registration?attempted=${encodeURIComponent(result.outcome ?? "unknown")}`, { replace: true });
    } catch (err) {
      toast.error(err instanceof ApiError ? (err.detail ?? err.title ?? "The registration attempt failed.") : "The registration attempt failed.");
    } finally {
      setBusy(false);
    }
  }

  async function handleRetryAll() {
    setBusy(true);
    try {
      const counts = await retryAwaitingRegistrations();
      const q = new URLSearchParams({
        awaiting: String(counts.awaiting ?? ""),
        retried: String(counts.retried ?? ""),
        registered: String(counts.registered ?? ""),
        waiting: String(counts.waiting ?? ""),
        exhausted: String(counts.exhausted ?? ""),
      });
      navigate(`${href}/registration?${q}`, { replace: true });
    } catch (err) {
      toast.error(err instanceof ApiError ? (err.detail ?? err.title ?? "The retry attempt failed.") : "The retry attempt failed.");
    } finally {
      setBusy(false);
    }
  }

  async function handleCancel(formData: FormData) {
    setCancelError(undefined);
    try {
      await cancelIrn(id, String(formData.get("reason") ?? "").trim());
      navigate(`${href}/registration?cancelled=1`, { replace: true });
    } catch (err) {
      const closed = windowClosedAt(err);
      if (closed !== null) {
        navigate(`${href}/registration?windowPassed=1&closedAt=${encodeURIComponent(closed)}`, { replace: true });
        return;
      }
      setCancelError(err instanceof ApiError ? (err.detail ?? err.title ?? "The service refused this cancellation.") : "The service refused this cancellation.");
    }
  }

  return (
    <div className="@container flex flex-col gap-6">
      <PageHero
        breadcrumb={[{ name: "Home", href: "/" }, { name: "Invoicing", href: "/invoicing" }, { name: "Invoice", href }, { name: "E-Invoice Registration" }]}
        back={{ href, label: "Back to the invoice" }}
        icon={<SealIcon aria-hidden />}
        iconClassName={TILE[state]}
        title="E-Invoice Registration"
        description="Whether this invoice carries an IRN, why not, and what can be done about it."
        meta={
          <>
            <Badge variant="outline" rounded="pill" className="border-muted capitalize text-gray-700">
              {statusWords(word)}
            </Badge>
            <Badge variant="flat" rounded="pill" className="gap-1.5 bg-gray-100 text-gray-700">
              <CountIcon aria-hidden className="size-4" />
              {data.attempts.length} {data.attempts.length === 1 ? "attempt" : "attempts"}
            </Badge>
          </>
        }
        actions={
          <>
            {state === "registered" ? (
              <Link to={`${href}/print`} target="_blank" rel="noopener">
                <Button as="span" variant="outline">
                  <PrintIcon aria-hidden className="me-1.5 size-4" />
                  Print with IRN and QR
                </Button>
              </Link>
            ) : null}
            {mayAttempt ? (
              <>
                <Button type="button" variant="outline" disabled={busy} onClick={handleRetryAll}>
                  <RetryIcon aria-hidden className="me-1.5 size-4" />
                  Retry everything awaiting registration
                </Button>
                <Button type="button" isLoading={busy} onClick={handleRegister}>
                  <SendIcon aria-hidden className="me-1.5 size-4" />
                  Register this invoice now
                </Button>
              </>
            ) : null}
            {state === "registered" && mayCancel ? (
              <Dropdown placement="bottom-end">
                <Dropdown.Trigger aria-label="More actions for this registration" className={iconMenuTriggerClass}>
                  <MoreIcon aria-hidden className="size-4" />
                </Dropdown.Trigger>
                <Dropdown.Menu className="w-52">
                  <Dropdown.Item onClick={() => navigate(`${href}/registration?confirm=cancel`)} className="gap-2.5 text-sm text-red-dark">
                    <CancelIcon aria-hidden className="size-4 shrink-0" />
                    Cancel this IRN
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            ) : null}
          </>
        }
      />

      <Outcome searchParams={searchParams} id={id} />

      <ReasonDialog
        title="Cancel this IRN"
        open={confirmingCancel && mayCancel}
        consequence={
          <>If the portal&rsquo;s window is still open the IRN and the invoice are cancelled together. If it has passed, nothing is cancelled and a credit note is the remaining remedy — this screen will say which happened.</>
        }
        action={handleCancel}
        confirmLabel="Cancel the IRN"
        error={cancelError}
        cancel={
          <Button type="button" variant="outline" onClick={() => setSearchParams((p) => (p.delete("confirm"), p))}>
            Keep it
          </Button>
        }
      />

      <div className="flex flex-col gap-6 @5xl:grid @5xl:grid-cols-12 @5xl:items-start">
        <div className="flex flex-col gap-6 @5xl:col-span-8">
          <WidgetCard
            rounded="xl"
            title={
              <CardTitle icon={<SealIcon aria-hidden className="size-5" />} tile="bg-green-lighter text-green-dark">
                Registration status
              </CardTitle>
            }
            headerClassName="items-center"
          >
            <div className="mt-5">
              <RegistrationState state={state} irn={data.irn ?? undefined} reason={state === "failed" ? (data.irnError ?? undefined) : undefined} window={<>Reported by the service as “{statusWords(word)}”</>}>
                <div className="flex flex-col gap-4">
                  {data.irnError && state !== "failed" ? <VerbatimErrorText label="The registration service's retained response">{data.irnError}</VerbatimErrorText> : null}
                  {state === "pending" ? (
                    <Text className="text-sm text-gray-600">
                      While this invoice is awaiting registration it is <strong>not</strong> queued for Tally.
                    </Text>
                  ) : null}
                  {state === "registered" ? (
                    <Text className="text-sm text-gray-600">
                      The cancellation window belongs to the registration portal and is not published to this screen. Attempting the cancellation is what reports whether it is still open.
                    </Text>
                  ) : null}
                </div>
              </RegistrationState>
            </div>
          </WidgetCard>

          <Attempts rows={data.attempts} />
        </div>

        <WidgetCard
          rounded="xl"
          className="@5xl:col-span-4"
          title={
            <CardTitle icon={<InfoIcon aria-hidden className="size-5" />} tile="bg-blue-lighter text-blue-dark">
              At a glance
            </CardTitle>
          }
          headerClassName="items-center"
        >
          <div className="mt-5 flex flex-col gap-5">
            <InfoBlock icon={<SealIcon aria-hidden className="size-4" />} tile="bg-green-lighter text-green-dark" label="IRN">
              {data.irn ? (
                <span className="flex items-center gap-1">
                  <Text as="span" className="min-w-0 break-all font-mono text-xs text-gray-900">
                    {data.irn}
                  </Text>
                  <CopyButton value={data.irn} label="Copy the IRN" />
                </span>
              ) : (
                <Text className="text-sm text-gray-500">None on this invoice</Text>
              )}
            </InfoBlock>
            <InfoBlock icon={<NextIcon aria-hidden className="size-4" />} tile="bg-orange-lighter text-orange-dark" label="Next attempt">
              <Text className="text-sm text-gray-900">
                {data.nextAttemptDueAt ? `Not before ${formatDate(data.nextAttemptDueAt)}` : "None scheduled"}
              </Text>
            </InfoBlock>
            <InfoBlock icon={<HistoryIcon aria-hidden className="size-4" />} tile="bg-secondary-lighter text-secondary-dark" label="Last attempt">
              <Text className="text-sm text-gray-900">
                {data.attempts.length > 0 ? formatMoment(data.attempts[data.attempts.length - 1]!.startedAt) : "Never attempted"}
              </Text>
            </InfoBlock>
          </div>
        </WidgetCard>
      </div>
    </div>
  );
}

function Outcome({ searchParams, id }: { searchParams: URLSearchParams; id: string }) {
  if (searchParams.get("windowPassed") === "1") {
    const closedAt = searchParams.get("closedAt");
    return (
      <div role="alert">
        <CaveatBanner
          caveat={<>The registration portal&rsquo;s cancellation window has passed. The IRN was not cancelled.</>}
          action={
            <Link to={`/invoicing/credit-notes?invoiceId=${id}`} className="font-medium underline">
              Raise a credit note against this invoice
            </Link>
          }
        >
          <div className="flex flex-col gap-2 text-sm">
            {closedAt ? <Text className="text-gray-500">It closed on {formatDate(closedAt)}.</Text> : null}
            <Text>A credit note is the remaining remedy, and raising one is what keeps the ledger right.</Text>
          </div>
        </CaveatBanner>
      </div>
    );
  }

  if (searchParams.get("cancelled") === "1") {
    return (
      <div role="status">
        <Alert color="success" variant="flat" bar icon={<DoneIcon aria-hidden className="size-5" />}>
          The portal accepted the cancellation. The IRN and the invoice are cancelled.
        </Alert>
      </div>
    );
  }

  const attempted = searchParams.get("attempted");
  if (attempted) {
    return (
      <div role="status">
        <Alert color="info" variant="flat" bar icon={<InfoIcon aria-hidden className="size-5" />}>
          The service reported that attempt as &ldquo;{attempted}&rdquo;.
        </Alert>
      </div>
    );
  }

  if (searchParams.get("retried") !== null) {
    return (
      <div role="status">
        <Alert color="info" variant="flat" bar icon={<RetryIcon aria-hidden className="size-5" />}>
          Awaiting {searchParams.get("awaiting")}, retried {searchParams.get("retried")}, registered {searchParams.get("registered")}, waiting on a backoff{" "}
          {searchParams.get("waiting")}, out of attempts {searchParams.get("exhausted")}.
        </Alert>
      </div>
    );
  }

  return null;
}

/** Outcome words this screen recognises well enough to color; anything else stays uncolored rather than guessed at. */
const ATTEMPT_TIMELINE_STATUS: Record<string, "success" | "canceled"> = { registered: "success", failed: "canceled" };

function Attempts({ rows }: { rows: RegistrationStatus["attempts"] }) {
  const errored = rows.filter((row) => row.error);

  return (
    <WidgetCard
      rounded="xl"
      title={
        <CardTitle icon={<HistoryIcon aria-hidden className="size-5" />} tile="bg-orange-lighter text-orange-dark">
          Attempts
        </CardTitle>
      }
      action={
        rows.length > 0 ? (
          <Badge variant="flat" color="warning" rounded="pill">
            {rows.length}
          </Badge>
        ) : null
      }
      headerClassName="items-center"
    >
      {rows.length === 0 ? (
        <div className="pt-5">
          <EmptyState title="No attempts yet">Each attempt to register this invoice appears here, with the portal&rsquo;s own answer.</EmptyState>
        </div>
      ) : (
        <div className="mt-6 flex flex-col gap-4">
          <Timeline
            order="desc"
            data={rows.map((row) => ({
              title: `Attempt ${row.attemptNo}`,
              text: row.outcome ?? "in flight",
              hightlightedText: row.resolvedByStatusQuery ? "The response was lost and a status query settled it — the number was not obtained twice." : "",
              date: formatDate(row.startedAt),
              time: formatTime(row.startedAt) ?? "",
              status: row.outcome ? ATTEMPT_TIMELINE_STATUS[row.outcome] : undefined,
            }))}
          />
          {errored.map((row) => (
            <div key={String(row.attemptNo)} className="flex items-start gap-2.5">
              <WarningIcon aria-hidden className="mt-1 size-4 shrink-0 text-red-dark" />
              <div className="min-w-0 flex-1">
                <VerbatimErrorText label={`Attempt ${row.attemptNo} — the registration service's response`}>{row.error!}</VerbatimErrorText>
              </div>
            </div>
          ))}
        </div>
      )}
    </WidgetCard>
  );
}
